import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { adminHelpCreateAction, adminHelpGetAction, adminHelpUpdateAction } from "../Action/action";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import CKEditorWithSource from "./CKEditorWithSource";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);
    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });
    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function HelpQuestionForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [loadingRow, setLoadingRow] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [question, setQuestion] = useState("");
  const [solution, setSolution] = useState("");
  const [isActive, setIsActive] = useState(1);
  const [sortOrder, setSortOrder] = useState("0");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isEdit) return;
      setLoadingRow(true);
      setError("");
      try {
        const res = await adminHelpGetAction(id);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load help question.");
          return;
        }
        const r = res.data || {};
        setQuestion(r.question || "");
        setSolution(r.solution || "");
        setIsActive(r.is_active !== undefined && r.is_active !== null ? (Number(r.is_active) ? 1 : 0) : 1);
        const ord = r.sort_order;
        setSortOrder(
          ord != null && ord !== "" && Number.isFinite(Number(ord)) ? String(parseInt(String(ord), 10)) : "0"
        );
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load help question.");
      } finally {
        if (!cancelled) setLoadingRow(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const validate = () => {
    const errs = {};
    if (!String(question || "").trim()) errs.question = "Question is required.";
    if (!editorPlainText(solution)) errs.solution = "Solution is required.";
    const ordRaw = String(sortOrder ?? "").replace(/[^\d-]/g, "");
    const ord = ordRaw === "" || ordRaw === "-" ? 0 : parseInt(ordRaw, 10);
    if (!Number.isFinite(ord)) errs.sortOrder = "Sort order must be a number.";
    return errs;
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the required fields.", { toastId: "help-save" });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const ordStr = String(sortOrder ?? "").replace(/[^\d-]/g, "");
      const sort_order = ordStr === "" || ordStr === "-" ? 0 : parseInt(ordStr, 10);
      const body = {
        question: String(question).trim(),
        solution: solution || "",
        is_active: Number(isActive),
        sort_order: Number.isFinite(sort_order) ? sort_order : 0
      };
      const res = isEdit ? await adminHelpUpdateAction(id, body) : await adminHelpCreateAction(body);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Save failed");
      toast.success(isEdit ? "Help question updated" : "Help question created", { toastId: "help-save" });
      navigate("/help-questions");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "help-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {isEdit && loadingRow ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save help question.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/help-questions">Help</Link>
          <span>›</span>
          <span>{isEdit ? "Edit question" : "Add question"}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/help-questions" className="btn btn-outline btn-sm">
            ← Back
          </Link>
        </div>
      </div>

      <div className="card mb-6">
        <h2 className="section-title">Help question</h2>

        <div className="form-group">
          <label>
            Question <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Notifications not working?"
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              setFieldErrors((p) => ({ ...p, question: "" }));
            }}
            aria-invalid={!!fieldErrors.question}
            style={fieldErrors.question ? { borderColor: "var(--danger)" } : undefined}
          />
          {fieldErrors.question ? (
            <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
              {fieldErrors.question}
            </div>
          ) : null}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Status</label>
            <select className="form-control" value={isActive} onChange={(e) => setIsActive(Number(e.target.value))}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
          <div className="form-group">
            <label>Sort order</label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="0"
              value={sortOrder}
              onInput={(e) => {
                const v = e.target.value.replace(/[^\d-]/g, "");
                setSortOrder(v);
                setFieldErrors((p) => ({ ...p, sortOrder: "" }));
              }}
              aria-invalid={!!fieldErrors.sortOrder}
              style={fieldErrors.sortOrder ? { borderColor: "var(--danger)" } : undefined}
            />
            {fieldErrors.sortOrder ? (
              <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                {fieldErrors.sortOrder}
              </div>
            ) : (
              <p className="small-text mt-2">Lower numbers appear first in the app.</p>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>
            Solution <span className="text-danger">*</span>
          </label>
          <CKEditorWithSource
            heightClassName="ckeditor-height-200"
            containerStyle={
              fieldErrors.solution
                ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
            }
            config={{ extraPlugins: [CkUploadAdapterPlugin] }}
            data={solution}
            onChange={(html) => {
              setSolution(html);
              setFieldErrors((p) => ({ ...p, solution: "" }));
            }}
          />
          {fieldErrors.solution ? (
            <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
              {fieldErrors.solution}
            </div>
          ) : null}
        </div>
      </div>

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || loadingRow}>
          {saving ? "Saving…" : isEdit ? "Update Help Question" : "Save Help Question"}
        </button>
        <Link to="/help-questions" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
